/**
 * Performance Monitoring Utilities
 * 
 * 性能監控工具
 * 用於收集和分析應用性能指標
 */

/**
 * 性能指標收集器
 */
class PerformanceCollector {
  constructor() {
    this.metrics = new Map();
    this.observers = new Map();
  }

  /**
   * 收集 Web Vitals 指標
   * @param {Object} metric - Web Vitals 指標對象
   */
  collectWebVital(metric) {
    const { name, value, id, delta, rating } = metric;
    
    // 存儲指標
    this.metrics.set(id, {
      name,
      value,
      delta,
      rating,
      timestamp: Date.now(),
    });

    // 發送到分析服務
    this.sendToAnalytics(metric);

    // 記錄到控制台（開發環境）
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Web Vital] ${name}:`, {
        value: value.toFixed(2),
        rating,
        delta: delta?.toFixed(2),
      });
    }
  }

  /**
   * 發送指標到分析服務
   * @param {Object} metric - Web Vitals 指標對象
   */
  sendToAnalytics(metric) {
    // 發送到 Google Analytics 4
    const GA4_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;
    if (GA4_MEASUREMENT_ID && window.gtag) {
      window.gtag('event', metric.name, {
        value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
        event_label: metric.id,
        non_interaction: true,
        metric_id: metric.id,
        metric_value: metric.value,
        metric_delta: metric.delta,
        metric_rating: metric.rating,
      });
    }

    // 發送到 PostHog
    const POSTHOG_API_KEY = process.env.REACT_APP_POSTHOG_API_KEY;
    if (POSTHOG_API_KEY && window.posthog) {
      window.posthog.capture('web_vital', {
        name: metric.name,
        value: metric.value,
        delta: metric.delta,
        id: metric.id,
        rating: metric.rating,
      });
    }

    // 發送到 Sentry（性能監控）
    const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
    if (SENTRY_DSN && process.env.NODE_ENV === 'production' && window.Sentry) {
      window.Sentry.metrics.distribution(metric.name, metric.value, {
        unit: metric.name === 'CLS' ? 'ratio' : 'millisecond',
        tags: {
          rating: metric.rating,
        },
      });
    }
  }

  /**
   * 測量自定義性能指標
   * @param {string} name - 指標名稱
   * @param {number} value - 指標值
   * @param {Object} tags - 標籤
   */
  measureCustomMetric(name, value, tags = {}) {
    const metric = {
      name,
      value,
      tags,
      timestamp: Date.now(),
    };

    this.metrics.set(`${name}_${Date.now()}`, metric);

    // 發送到分析服務
    if (window.gtag) {
      window.gtag('event', 'custom_metric', {
        metric_name: name,
        metric_value: value,
        ...tags,
      });
    }

    if (window.posthog) {
      window.posthog.capture('custom_metric', {
        name,
        value,
        ...tags,
      });
    }
  }

  /**
   * 開始性能測量
   * @param {string} name - 測量名稱
   * @returns {Function} 停止測量的函數
   */
  startMeasure(name) {
    const startTime = performance.now();
    
    return () => {
      const duration = performance.now() - startTime;
      this.measureCustomMetric(name, duration);
      return duration;
    };
  }

  /**
   * 獲取所有指標
   * @returns {Array} 所有指標的數組
   */
  getAllMetrics() {
    return Array.from(this.metrics.values());
  }

  /**
   * 獲取指定指標
   * @param {string} name - 指標名稱
   * @returns {Array} 匹配的指標
   */
  getMetricsByName(name) {
    return this.getAllMetrics().filter(m => m.name === name);
  }

  /**
   * 清除所有指標
   */
  clearMetrics() {
    this.metrics.clear();
  }
}

// 創建全局實例
export const performanceCollector = new PerformanceCollector();

/**
 * 初始化性能監控
 */
export function initPerformanceMonitoring() {
  if (typeof window === 'undefined' || !window.performance) {
    return;
  }

  // 監聽 Web Vitals
  import('web-vitals').then(({ onCLS, onFID, onFCP, onLCP, onTTFB, onINP }) => {
    onCLS((metric) => performanceCollector.collectWebVital(metric));
    onFID((metric) => performanceCollector.collectWebVital(metric));
    onFCP((metric) => performanceCollector.collectWebVital(metric));
    onLCP((metric) => performanceCollector.collectWebVital(metric));
    onTTFB((metric) => performanceCollector.collectWebVital(metric));
    
    // INP (Interaction to Next Paint) 替代 FID
    if (onINP) {
      onINP((metric) => performanceCollector.collectWebVital(metric));
    }
  }).catch((err) => {
    console.warn('Failed to initialize Web Vitals:', err);
  });

  // 監聽資源載入性能
  if ('PerformanceObserver' in window) {
    try {
      const resourceObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'resource') {
            const { name, duration, transferSize, decodedBodySize } = entry;
            
            // 只記錄較慢的資源（超過 1 秒）
            if (duration > 1000) {
              performanceCollector.measureCustomMetric('slow_resource', duration, {
                resource_name: name,
                transfer_size: transferSize,
                decoded_size: decodedBodySize,
              });
            }
          }
        }
      });

      resourceObserver.observe({ entryTypes: ['resource'] });
    } catch (err) {
      console.warn('Failed to observe resource performance:', err);
    }
  }
}

/**
 * 測量函數執行時間
 * @param {Function} fn - 要測量的函數
 * @param {string} name - 測量名稱
 * @returns {Function} 包裝後的函數
 */
export function measureFunction(fn, name) {
  return function(...args) {
    const stopMeasure = performanceCollector.startMeasure(name);
    try {
      const result = fn.apply(this, args);
      
      // 如果是 Promise，等待完成
      if (result instanceof Promise) {
        return result.finally(() => stopMeasure());
      }
      
      stopMeasure();
      return result;
    } catch (error) {
      stopMeasure();
      throw error;
    }
  };
}

/**
 * React 組件性能測量 HOC
 * @param {React.Component} Component - 要測量的組件
 * @param {string} name - 組件名稱
 * @returns {React.Component} 包裝後的組件
 */
export function withPerformanceMeasure(Component, name) {
  return function MeasuredComponent(props) {
    // 動態導入 React 以避免循環依賴
    const React = require('react');
    const stopMeasure = performanceCollector.startMeasure(`component_render_${name}`);
    
    React.useEffect(() => {
      return () => {
        stopMeasure();
      };
    });

    return React.createElement(Component, props);
  };
}

export default performanceCollector;

